// Civ-style strategy camera: a target on the map, a height above it, a pitch that eases from a
// close tilt toward top-down as the camera rises, critically damped springs on everything, and a
// horizontally wrapping world.
//
//            camera ●
//                    ╲  pitch (50° close → 80° far)
//          height h   ╲
//                      ╲
//   ────────────────────● target (x, z) on the map ───── +x east, +z south
//                back = h / tan(pitch), rotated by yaw (0 = looking north)
//
// Inputs: WASD / arrows and screen-edge pan, drag pan (grab the ground), wheel zoom toward the
// cursor, Q/E yaw ±30° that springs back on release, and flyTo() arcs over ~1.2 s.
//
// Above GLOBE.from the flat map curls into a globe (src/view/globe.ts draws it). The camera keeps
// the same target and height there: the target is the point facing the viewer, dragging rotates
// the sphere so the grabbed point stays under the cursor, and the wheel zooms back into the flat
// map at the point under the cursor.
//
// The flat view is kept inside the map: the ground under the four frustum corners never crosses
// the north or south edge, so a glancing pitch near a pole cannot show the void beyond it.

import * as THREE from 'three/webgpu';
import { K, ROWS, ROW_H, WORLD_H, WORLD_W, lonLatToWorld, pattersonY, worldToLonLat, wrapX, type LonLat } from '../core/grid.ts';
import { centreFor, fromScreenPoint, type ScreenPoint, type Spot } from './globe-math.ts';

export const CAMERA = {
  fov: 35,
  minHeight: 3.5,
  maxHeight: 1400,
  closePitch: 50,
  farPitch: 80,
  pitchEaseFrom: 22,
  pitchEaseTo: 420,
  panOmega: 9,
  heightOmega: 7,
  yawOmega: 6,
  pitchOmega: 7,
  /** Keyboard/edge pan speed, in camera heights per second. */
  panSpeed: 0.9,
  edgePx: 14,
  yawStepDeg: 30,
  zoomPerWheel: 0.0014,
  flyMs: 1200,
  /** Extra height at mid-flight per world unit travelled. */
  flyArc: 0.35,
  flyArcMax: 260,
  /** Ground plane used for cursor rays. */
  groundY: 0.1,
  dragThresholdPx: 4,
} as const;

/** The flat map curls into a globe between two camera heights (hex radii). */
export const GLOBE = {
  from: 820,
  to: 1250,
  /** Sphere radius in world units. */
  radius: 400,
  /** Highest latitude (degrees) the globe centres on, so north stays up. */
  maxLat: 84,
  /** Pitch at full globe: straight down at the sphere. */
  pitch: 90,
  /** The sphere takes over picking and dragging from this morph amount. */
  grabFrom: 0.5,
} as const;

const DEG = Math.PI / 180;
const MAP_H = ROWS * ROW_H;
const SETTLE_EPS = 1e-3;
const INITIAL_HEIGHT = 656;

// Flat view limits. Haze hides the map beyond this slant range (camera heights), so the ground
// past it cannot show an edge. Equal to lighting.ts HAZE_FULL_PER_HEIGHT, where the haze turns opaque.
//
// Two limits with two fades:
//   pitch floor    fades out between PITCH_FADE heights (the whole map fits the screen there, so a
//                  glancing pitch is harmless, and only shots and the director set one)
//   target clamp   holds at every flat height, so the ground under the frustum stays inside the map
//                  (no void band above a polar view); it lets go over PIN_FADE, the first stretch of
//                  the globe morph, which needs the whole latitude range to turn
const HAZE_REACH = 11;
const PITCH_FADE = [300, 600] as const;
const PIN_FADE = [GLOBE.from, GLOBE.from + 100] as const;
const RAY_EPS = 1e-6;

// A wheel gesture keeps the ground it began on (seconds it stays live after the last notch, and how far
// the cursor may stray, in px). See CivCamera.anchor.
const ANCHOR_HOLD = 0.6;
const ANCHOR_SLOP_PX = 2;

// The haze closes in as the view glances (looking along the ground there is more air in the way),
// so a low pitch sees its horizon within the map: full range from HAZE_TILT[1], HAZE_GLANCE of it
// at HAZE_TILT[0] and below.
const HAZE_TILT = [20, 50] as const;
const HAZE_GLANCE = 0.3;

// A glancing pitch is raised until the visible ground fits the map (this share of its height), so
// no north or south edge and no empty sky can show. Bisection bounds and step count.
const FIT_SHARE = 0.94;
const FIT_PITCH_MIN = 8;
const FIT_STEPS = 14;
const CORNERS = [[-1, -1], [-1, 1], [1, -1], [1, 1]] as const;

/** Where the camera looks and from how high. Angles in degrees. */
export interface CameraPose {
  x: number;
  z: number;
  height: number;
  yaw: number;
  pitch: number;
}

interface Spring {
  v: number;
  vel: number;
  goal: number;
}

function spring(v: number, goal: number): Spring {
  return { v, vel: 0, goal };
}

/** Critically damped spring step (exact solution, stable for any dt). */
function step(s: Spring, omega: number, dt: number): void {
  const x0 = s.v - s.goal;
  const e = Math.exp(-omega * dt);
  const t = (s.vel + omega * x0) * dt;
  s.v = s.goal + (x0 + t) * e;
  s.vel = (s.vel - omega * t) * e;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Shortest signed x distance on the wrapping world. */
export function wrapDelta(from: number, to: number): number {
  let d = (to - from) % WORLD_W;
  if (d > WORLD_W / 2) {
    d -= WORLD_W;
  }
  if (d < -WORLD_W / 2) {
    d += WORLD_W;
  }
  return d;
}

/** How far the flat map has curled into a globe at a camera height: 0 flat, 1 globe. */
export function globeAmount(height: number): number {
  return smoothstep(GLOBE.from, GLOBE.to, height);
}

/** Multiplier for the haze distances at a pitch (degrees): 1 at Civ's close tilt, less when glancing. */
export function hazeScale(pitch: number): number {
  return HAZE_GLANCE + (1 - HAZE_GLANCE) * smoothstep(HAZE_TILT[0], HAZE_TILT[1], pitch);
}

/** Default pitch for a camera height. */
export function pitchFor(height: number): number {
  const flat = CAMERA.closePitch + (CAMERA.farPitch - CAMERA.closePitch) * smoothstep(CAMERA.pitchEaseFrom, CAMERA.pitchEaseTo, height);
  return flat + (GLOBE.pitch - flat) * globeAmount(height);
}

/** What a flight can carry beyond its target: a duration, and the pitch and yaw to arrive with. */
export interface FlightOptions {
  seconds?: number;
  /** Degrees; eased along the flight (omitted keeps the automatic pitch). */
  pitch?: number;
  /** Degrees; eased along the flight (omitted keeps the current yaw). */
  yaw?: number;
}

interface Flight {
  start: number;
  /** Seconds the flight lasts. */
  dur: number;
  yaw0: number;
  yaw1: number | null;
  pitch0: number;
  pitch1: number | null;
  x0: number;
  z0: number;
  h0: number;
  x1: number;
  z1: number;
  h1: number;
  arc: number;
}

const PAN_KEYS: Record<string, readonly [number, number]> = {
  KeyW: [0, 1],
  ArrowUp: [0, 1],
  KeyS: [0, -1],
  ArrowDown: [0, -1],
  KeyA: [-1, 0],
  ArrowLeft: [-1, 0],
  KeyD: [1, 0],
  ArrowRight: [1, 0],
};

/** A wheel gesture: the ground under the cursor when it began, and where the target z would sit unclamped. */
interface Anchor {
  px: number;
  py: number;
  until: number;
  ground: THREE.Vector3;
  z: number;
  /** The goal z as this gesture last set it; a different value means something else moved the camera. */
  written: number;
}

export class CivCamera {
  readonly camera = new THREE.PerspectiveCamera(CAMERA.fov, 1, 0.1, 5000);
  private readonly x = spring(WORLD_W / 2, WORLD_W / 2);
  private readonly z = spring(MAP_H / 2, MAP_H / 2);
  private readonly h = spring(INITIAL_HEIGHT, INITIAL_HEIGHT);
  private readonly yaw = spring(0, 0);
  private readonly pitch = spring(pitchFor(INITIAL_HEIGHT), pitchFor(INITIAL_HEIGHT));
  private pitchOverride: number | null = null;
  private flight: Flight | null = null;
  private readonly keys = new Set<string>();
  private pointer: { x: number; y: number; inside: boolean } = { x: 0, y: 0, inside: false };
  private anchor: Anchor | null = null;
  /** The clamp holds z where its goal cannot be reached (see constrain). */
  private zPinned = false;
  private drag: { id: number; startX: number; startY: number; ground: THREE.Vector3; grab: Spot | null; moved: boolean } | null = null;
  private dom: HTMLElement | null = null;
  private readonly ray = new THREE.Raycaster();
  private readonly plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -CAMERA.groundY);
  private readonly sphere = new THREE.Sphere();
  private readonly hit = new THREE.Vector3();

  /** Seconds since start (driven by update); used by flights. */
  private clock = 0;

  /**
   * The ground point under a client pixel, or null when the ray misses the ground plane. On the
   * globe it is the flat-map position of the sphere point under the pixel, so zooming and picking
   * work the same at every height.
   */
  groundAt(clientX: number, clientY: number, out = new THREE.Vector3()): THREE.Vector3 | null {
    if (!this.castRay(clientX, clientY)) {
      return null;
    }
    if (this.globeAmount() < GLOBE.grabFrom) {
      return this.ray.ray.intersectPlane(this.plane, out);
    }
    const spot = this.spotAt(clientX, clientY);
    if (!spot) {
      return null;
    }
    const at = lonLatToWorld(spot.lon / DEG, spot.lat / DEG);
    return out.set(at.x, CAMERA.groundY, at.z);
  }

  /** How far the flat map has curled into a globe: 0 flat, 1 globe. */
  globeAmount(): number {
    return globeAmount(this.h.v);
  }

  /** The longitude/latitude (degrees) under a client pixel on the globe, or null off the sphere. */
  lonLatAt(clientX: number, clientY: number): LonLat | null {
    const spot = this.spotAt(clientX, clientY);
    return spot ? { lon: spot.lon / DEG, lat: spot.lat / DEG } : null;
  }

  /** The spot the globe faces (the camera target), in radians. */
  centre(): Spot {
    const c = worldToLonLat(this.x.v, this.z.v);
    return { lon: c.lon * DEG, lat: c.lat * DEG };
  }

  private castRay(clientX: number, clientY: number): boolean {
    if (!this.dom) {
      return false;
    }
    const r = this.dom.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    this.ray.setFromCamera(ndc, this.camera);
    return true;
  }

  /** The cursor ray against the globe, as a unit-sphere point in the globe's screen frame. */
  private sphereHit(clientX: number, clientY: number): ScreenPoint | null {
    if (!this.castRay(clientX, clientY)) {
      return null;
    }
    const c = this.sphere.center.set(this.x.v, CAMERA.groundY - GLOBE.radius, this.z.v);
    this.sphere.radius = GLOBE.radius;
    const hit = this.ray.ray.intersectSphere(this.sphere, this.hit);
    if (!hit) {
      return null;
    }
    // World (east, up, south) to the globe frame (east, north, toward the viewer).
    return { x: (hit.x - c.x) / GLOBE.radius, y: -(hit.z - c.z) / GLOBE.radius, z: (hit.y - c.y) / GLOBE.radius };
  }

  private spotAt(clientX: number, clientY: number): Spot | null {
    const p = this.sphereHit(clientX, clientY);
    return p ? fromScreenPoint(p, this.centre()) : null;
  }

  /** Listen for input on `dom`; returns a function that removes the listeners. */
  attach(dom: HTMLElement): () => void {
    this.dom = dom;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (isTyping(e)) {
        return;
      }
      this.keys.add(e.code);
      if (e.code === 'KeyQ') {
        this.yaw.goal = CAMERA.yawStepDeg;
      } else if (e.code === 'KeyE') {
        this.yaw.goal = -CAMERA.yawStepDeg;
      }
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      this.keys.delete(e.code);
      if (e.code === 'KeyQ' || e.code === 'KeyE') {
        this.yaw.goal = 0;
      }
    };
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      this.zoomAt(e.clientX, e.clientY, Math.exp(e.deltaY * CAMERA.zoomPerWheel));
    };
    const onDown = (e: PointerEvent): void => {
      if (e.button !== 0) {
        return;
      }
      const ground = this.groundAt(e.clientX, e.clientY);
      if (!ground) {
        return;
      }
      this.flight = null;
      const grab = this.globeAmount() >= GLOBE.grabFrom ? this.spotAt(e.clientX, e.clientY) : null;
      this.drag = { id: e.pointerId, startX: e.clientX, startY: e.clientY, ground, grab, moved: false };
    };
    const onMove = (e: PointerEvent): void => {
      const r = dom.getBoundingClientRect();
      this.pointer = { x: e.clientX - r.left, y: e.clientY - r.top, inside: true };
      if (!this.drag || e.pointerId !== this.drag.id) {
        return;
      }
      const moved = Math.hypot(e.clientX - this.drag.startX, e.clientY - this.drag.startY);
      if (moved > CAMERA.dragThresholdPx) {
        this.drag.moved = true;
      }
      if (!this.drag.moved) {
        return;
      }
      if (this.drag.grab) {
        this.rotateTo(this.drag.grab, e.clientX, e.clientY);
        return;
      }
      const now = this.groundAt(e.clientX, e.clientY);
      if (!now) {
        return;
      }
      this.jumpBy(this.drag.ground.x - now.x, this.drag.ground.z - now.z);
    };
    const onUp = (e: PointerEvent): void => {
      if (this.drag && e.pointerId === this.drag.id) {
        this.drag = null;
      }
    };
    const onLeave = (): void => {
      this.pointer.inside = false;
    };
    const onBlur = (): void => {
      this.keys.clear();
      this.yaw.goal = 0;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    dom.addEventListener('wheel', onWheel, { passive: false });
    dom.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    dom.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      dom.removeEventListener('wheel', onWheel);
      dom.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      dom.removeEventListener('pointerleave', onLeave);
      this.dom = null;
    };
  }

  /** True while the last pointer press has turned into a drag (so it is not a click). */
  dragging(): boolean {
    return this.drag?.moved === true;
  }

  /**
   * Zoom by `factor` (>1 = out) keeping the ground under the cursor in place. Notches of one gesture
   * scale about the ground the first one hit: where the view is held (the flat map's fit limits) the
   * pose under the cursor is not where the cursor started, and re-reading it would lose the region.
   */
  zoomAt(clientX: number, clientY: number, factor: number): void {
    this.flight = null;
    const before = this.h.goal;
    const after = Math.min(CAMERA.maxHeight, Math.max(CAMERA.minHeight, before * factor));
    this.h.goal = after;
    const a = this.anchorAt(clientX, clientY);
    if (!a) {
      return;
    }

    const pull = 1 - after / before;
    a.z += (a.ground.z - a.z) * pull;
    a.until = this.clock + ANCHOR_HOLD;
    this.x.goal += wrapDelta(this.x.goal, a.ground.x) * pull;
    this.z.goal = clampZ(a.z);
    a.written = this.z.goal;
  }

  /** The live gesture for this pixel, or a new one on the ground under it. */
  private anchorAt(clientX: number, clientY: number): Anchor | null {
    const a = this.anchor;
    const live = a !== null && this.clock < a.until && this.z.goal === a.written;
    if (a && live && Math.hypot(clientX - a.px, clientY - a.py) <= ANCHOR_SLOP_PX) {
      return a;
    }

    const ground = this.groundAt(clientX, clientY);
    this.anchor = ground ? { px: clientX, py: clientY, until: 0, ground, z: this.z.goal, written: this.z.goal } : null;
    return this.anchor;
  }

  /** Move target instantly (drag pan). */
  private jumpBy(dx: number, dz: number): void {
    this.x.v += dx;
    this.x.goal = this.x.v;
    this.x.vel = 0;
    this.z.v = clampZ(this.z.v + dz);
    this.z.goal = this.z.v;
    this.z.vel = 0;
    this.constrain();
    this.apply();
  }

  /** Turn the globe so the grabbed spot sits under the cursor. */
  private rotateTo(grab: Spot, clientX: number, clientY: number): void {
    const p = this.sphereHit(clientX, clientY);
    const c = p ? centreFor(grab, p, this.centre(), GLOBE.maxLat * DEG) : null;
    if (!c) {
      return;
    }
    const at = lonLatToWorld(c.lon / DEG, c.lat / DEG);
    this.jumpBy(wrapDelta(this.x.v, at.x), at.z - this.z.v);
  }

  /** Set the pose now (no animation). Omitted fields keep their value; pitch null = automatic. */
  jump(pose: Partial<Omit<CameraPose, 'pitch'>> & { pitch?: number | null }): void {
    this.flight = null;
    const set = (s: Spring, v: number | undefined): void => {
      if (v === undefined) {
        return;
      }
      s.v = v;
      s.goal = v;
      s.vel = 0;
    };
    set(this.x, pose.x === undefined ? undefined : wrapX(pose.x));
    set(this.z, pose.z === undefined ? undefined : clampZ(pose.z));
    set(this.h, pose.height === undefined ? undefined : clampH(pose.height));
    set(this.yaw, pose.yaw);
    if (pose.pitch !== undefined) {
      this.pitchOverride = pose.pitch;
    }
    set(this.pitch, this.pitchGoal());
    this.constrain();
    this.apply();
  }

  /** Arc to a point over ~1.2 s (or `seconds`); height defaults to the current one. */
  flyTo(x: number, z: number, height?: number, opts: FlightOptions = {}): void {
    const x1 = this.x.v + wrapDelta(this.x.v, x);
    const z1 = clampZ(z);
    const h1 = clampH(height ?? this.h.goal);
    const dist = Math.hypot(x1 - this.x.v, z1 - this.z.v);
    this.flight = {
      start: this.clock,
      dur: opts.seconds ?? CAMERA.flyMs / 1000,
      yaw0: this.yaw.v,
      yaw1: opts.yaw ?? null,
      pitch0: this.pitch.v,
      pitch1: opts.pitch ?? null,
      x0: this.x.v,
      z0: this.z.v,
      h0: this.h.v,
      x1,
      z1,
      h1,
      arc: Math.min(CAMERA.flyArcMax, dist * CAMERA.flyArc),
    };
  }

  /** Advance springs, keys, edge pan and flights by dt seconds, then place the camera. */
  update(dt: number): void {
    this.clock += dt;
    if (this.flight) {
      this.stepFlight();
    } else {
      this.stepInput(dt);
      step(this.x, CAMERA.panOmega, dt);
      step(this.z, CAMERA.panOmega, dt);
      step(this.h, CAMERA.heightOmega, dt);
    }
    step(this.yaw, CAMERA.yawOmega, dt);
    this.pitch.goal = this.pitchGoal();
    step(this.pitch, CAMERA.pitchOmega, dt);

    // Keep x inside [0, W): shift value and goal together so nothing jumps.
    if (this.x.v < 0 || this.x.v >= WORLD_W) {
      const shift = wrapX(this.x.v) - this.x.v;
      this.x.v += shift;
      this.x.goal += shift;
      if (this.flight) {
        this.flight.x0 += shift;
        this.flight.x1 += shift;
      }
    }
    this.constrain();
    this.apply();
  }

  /** True when no spring or flight is still moving. */
  settled(): boolean {
    if (this.flight) {
      return false;
    }
    const tol = SETTLE_EPS * Math.max(1, this.h.v);
    const still = (s: Spring): boolean => Math.abs(s.vel) < tol && (Math.abs(s.v - s.goal) < tol || (s === this.z && this.zPinned));
    return [this.x, this.z, this.h, this.yaw, this.pitch].every(still);
  }

  pose(): CameraPose {
    return { x: this.x.v, z: this.z.v, height: this.h.v, yaw: this.yaw.v, pitch: this.pitch.v };
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  private stepFlight(): void {
    const f = this.flight as Flight;
    const u = Math.min(1, (this.clock - f.start) / f.dur);
    const e = easeInOut(u);
    for (const [s, a, b] of [[this.x, f.x0, f.x1], [this.z, f.z0, f.z1]] as const) {
      s.v = a + (b - a) * e;
      s.goal = s.v;
      s.vel = 0;
    }
    this.h.v = f.h0 + (f.h1 - f.h0) * e + f.arc * Math.sin(Math.PI * u);
    this.h.goal = this.h.v;
    this.h.vel = 0;

    // A given yaw and pitch ride the same ease, so the view turns as it travels and lands exactly.
    if (f.yaw1 !== null) {
      this.yaw.v = f.yaw0 + (f.yaw1 - f.yaw0) * e;
      this.yaw.goal = this.yaw.v;
      this.yaw.vel = 0;
    }
    if (f.pitch1 !== null) {
      this.pitchOverride = f.pitch0 + (f.pitch1 - f.pitch0) * e;
      this.pitch.v = this.pitchGoal();
      this.pitch.goal = this.pitch.v;
      this.pitch.vel = 0;
    }
    if (u >= 1) {
      this.flight = null;
    }
  }

  private stepInput(dt: number): void {
    let fx = 0;
    let fz = 0;
    for (const code of this.keys) {
      const k = PAN_KEYS[code];
      if (k) {
        fx += k[0];
        fz += k[1];
      }
    }

    // Screen-edge pan while the pointer is inside the view.
    if (this.dom && this.pointer.inside && !this.drag) {
      const w = this.dom.clientWidth;
      const hgt = this.dom.clientHeight;
      if (this.pointer.x < CAMERA.edgePx) {
        fx -= 1;
      } else if (this.pointer.x > w - CAMERA.edgePx) {
        fx += 1;
      }
      if (this.pointer.y < CAMERA.edgePx) {
        fz += 1;
      } else if (this.pointer.y > hgt - CAMERA.edgePx) {
        fz -= 1;
      }
    }
    if (fx === 0 && fz === 0) {
      return;
    }

    // Screen right / forward on the ground, rotated by yaw.
    const yaw = this.yaw.v * DEG;
    const speed = this.h.v * CAMERA.panSpeed * dt;
    const rx = Math.cos(yaw);
    const rz = -Math.sin(yaw);
    const ux = -Math.sin(yaw);
    const uz = -Math.cos(yaw);
    this.x.goal += (fx * rx + fz * ux) * speed;
    this.z.goal = clampZ(this.z.goal + (fx * rz + fz * uz) * speed);
  }

  /** The pitch to ease toward: the wanted one, raised where a glancing view would leave the map. */
  private pitchGoal(): number {
    const wanted = this.pitchOverride ?? pitchFor(this.h.v);
    const strength = 1 - smoothstep(PITCH_FADE[0], PITCH_FADE[1], this.h.v);
    if (strength <= 0) {
      return wanted;
    }

    const fit = fitPitch(this.h.v, this.yaw.v, this.camera.aspect);
    return Math.max(wanted, wanted + (fit - wanted) * strength);
  }

  /**
   * Keep the view inside the map. Flat: the ground under the frustum corners stays between the
   * north and south edges (a view near a pole would otherwise look past the map). Where the view
   * is taller than the map (the last stretch before the globe) no target fits: it is held at the
   * middle of the range, so the sheet floats with an even margin, never a band on one side. The
   * goal keeps its place there, so the view moves to it once the range opens.
   * Globe: the centre stays under GLOBE.maxLat so north stays up.
   */
  private constrain(): void {
    const amount = this.globeAmount();
    const span = groundSpan(this.h.v, this.pitch.v, this.yaw.v, this.camera.aspect);
    const strength = 1 - smoothstep(PIN_FADE[0], PIN_FADE[1], this.h.v);
    let lo = -span.north * strength;
    let hi = MAP_H - span.south * strength;
    let goalLo = lo;
    let goalHi = hi;
    if (lo > hi) {
      lo = (lo + hi) / 2;
      hi = lo;
      goalLo = 0;
      goalHi = MAP_H;
    }

    // The flat limits give way to the pole limits as the map curls up.
    const north = zAtLat(GLOBE.maxLat);
    const south = zAtLat(-GLOBE.maxLat);
    lo += (north - lo) * amount;
    hi += (south - hi) * amount;
    goalLo += (north - goalLo) * amount;
    goalHi += (south - goalHi) * amount;
    const before = this.z.v;
    this.z.v = Math.min(hi, Math.max(lo, this.z.v));
    if (this.z.v !== before) {
      this.z.vel = 0;
    }

    // Pinned: the clamp holds the target where the goal cannot be reached (the goal keeps its
    // place for when the range opens), so the target will not move until something else does.
    this.zPinned = this.z.goal < lo || this.z.goal > hi;

    // A live wheel gesture sets the goal from where it would be unclamped.
    const a = this.anchor;
    const gesture = a !== null && this.clock < a.until && this.z.goal === a.written;
    this.z.goal = Math.min(goalHi, Math.max(goalLo, gesture ? a.z : this.z.goal));
    if (gesture) {
      a.written = this.z.goal;
    }
  }

  /** Place the three.js camera from the current spring values. */
  private apply(): void {
    const pitch = this.pitch.v * DEG;
    const yaw = this.yaw.v * DEG;
    const back = this.h.v / Math.tan(pitch);
    const cam = this.camera;
    cam.position.set(this.x.v + Math.sin(yaw) * back, this.h.v + CAMERA.groundY, this.z.v + Math.cos(yaw) * back);
    cam.up.set(0, 1, 0);
    cam.lookAt(this.x.v, CAMERA.groundY, this.z.v);
    cam.near = Math.max(0.05, this.h.v * 0.02);
    cam.far = this.h.v * 12 + 400;
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
  }
}

function clampZ(z: number): number {
  return Math.min(MAP_H, Math.max(0, z));
}

function clampH(h: number): number {
  return Math.min(CAMERA.maxHeight, Math.max(CAMERA.minHeight, h));
}

/** World z of a latitude (degrees). */
function zAtLat(lat: number): number {
  return WORLD_H / 2 - pattersonY(lat * DEG) * K;
}

/**
 * How far the visible ground reaches north and south of the target (z offsets: north is negative).
 * The four frustum corner rays are cast to the ground; a ray that misses it, or lands beyond the
 * haze, stops at the haze distance in its own direction.
 */
function groundSpan(height: number, pitchDeg: number, yawDeg: number, aspect: number): { north: number; south: number } {
  const pitch = pitchDeg * DEG;
  const yaw = yawDeg * DEG;
  const tanV = Math.tan((CAMERA.fov / 2) * DEG);
  const tanH = tanV * aspect;

  // Camera axes in world space: forward, right and up.
  const fx = -Math.sin(yaw) * Math.cos(pitch);
  const fy = -Math.sin(pitch);
  const fz = -Math.cos(yaw) * Math.cos(pitch);
  const rx = Math.cos(yaw);
  const rz = -Math.sin(yaw);
  const ux = -rz * fy;
  const uy = rz * fx - rx * fz;
  const uz = rx * fy;
  const camZ = (Math.cos(yaw) * height) / Math.tan(pitch);
  const reachMax = Math.sqrt((HAZE_REACH * hazeScale(pitchDeg)) ** 2 - 1) * height;

  let north = 0;
  let south = 0;
  for (const [sx, sy] of CORNERS) {
    const dx = fx + sx * tanH * rx + sy * tanV * ux;
    const dy = fy + sy * tanV * uy;
    const dz = fz + sx * tanH * rz + sy * tanV * uz;
    const flat = Math.hypot(dx, dz);
    const reach = dy < -RAY_EPS ? Math.min((height * flat) / -dy, reachMax) : reachMax;
    const z = camZ + (flat > RAY_EPS ? (dz / flat) * reach : 0);
    north = Math.min(north, z);
    south = Math.max(south, z);
  }
  return { north, south };
}

/**
 * The lowest pitch (degrees) at which the visible ground fits inside the map. The span shrinks as
 * the pitch rises, so this bisects between the lowest allowed pitch and straight down.
 */
function fitPitch(height: number, yawDeg: number, aspect: number): number {
  const room = MAP_H * FIT_SHARE;
  const fits = (pitch: number): boolean => {
    const span = groundSpan(height, pitch, yawDeg, aspect);
    return span.south - span.north <= room;
  };
  if (fits(FIT_PITCH_MIN)) {
    return FIT_PITCH_MIN;
  }

  let lo = FIT_PITCH_MIN;
  let hi = 90;
  for (let i = 0; i < FIT_STEPS; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) {
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return hi;
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
}
